import { useTheme, type Theme } from '@mui/material';
import React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { BarChartProps, BarChartType } from './types';

const DAILY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTHLY_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function getChartDataByType(type: BarChartType): { x: string; y: number }[] {
  switch (type) {
  case 'daily': {
    return DAILY_LABELS.map((x) => ({ x, y: 0 }));
  }
  case 'weekly': {
    return [
      { x: 'Week 1', y: 0 },
      { x: 'Week 2', y: 0 },
      { x: 'Week 3', y: 0 },
      { x: 'Week 4', y: 0 },
    ];
  }
  case 'monthly': {
    return MONTHLY_LABELS.map((x) => ({ x, y: 0 }));
  }
  default: {
    return DAILY_LABELS.map((x) => ({ x, y: 0 }));
  }
  }
}

const DEFAULT_BAR_COLOR = '#9EA0F0';

function darkenHex(hex: string, factor: number): string {
  const match = hex.replace('#', '').match(/.{2}/g);
  if (!match) return hex;
  const [r, g, b] = match.map((c) => Math.max(0, Math.round(parseInt(c, 16) * factor)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

const tickStyle = (theme: Theme) => ({
  fill: 'white',
  fontFamily: theme.typography.fontFamily,
  fontSize: '12px',
  fontWeight: theme.typography.body2.fontWeight,
});

export const BarChart: React.FC<BarChartProps> = ({ type, data, barColor = DEFAULT_BAR_COLOR, valueSuffix }) => {
  const theme = useTheme();
  const chartData = data ?? getChartDataByType(type);
  const formatValue = (value: number) => (valueSuffix != null ? `${value}${valueSuffix}` : String(value));
  const axisTickStyle = tickStyle(theme);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <RechartsBarChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={theme.palette.grey[800]} vertical={false} strokeOpacity={0.4} />
        <XAxis dataKey="x" stroke="white" tick={axisTickStyle} />
        <YAxis stroke="white" tick={axisTickStyle} tickFormatter={valueSuffix != null ? formatValue : undefined} />
        <Bar
          dataKey="y"
          fill={barColor}
          radius={[4, 4, 0, 0]}
          activeBar={{ fill: darkenHex(barColor, 0.7) }}
        >
          <LabelList
            dataKey="y"
            position="top"
            formatter={(value: unknown) => formatValue(Number(value))}
            fill="white"
            style={{ fontFamily: theme.typography.fontFamily, fontSize: theme.typography.body2.fontSize, fontWeight: 700 }}
          />
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};
